import { createStyles } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';

import Container from '~/domains/design-system/Container';
import { Routes } from '~/domains/common/constants/routes';
import type { FullContest } from '~/domains/contest/domains/services/interfaces/full-contest.interface';
import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';
import { FEATURE } from '~/common/enums/feature.enum';
import dt from '~/testing';

import GamecenterMarketplaceNavItem from './GamecenterMarketplaceNavItem';
import {
  GamecenterNonScheduledNavList,
  GamecenterNonScheduledNavListItem,
} from './GamecenterNonScheduledNavComponents';

const useStyles = createStyles((theme) => ({
  container: {
    background: theme.colors.gray[9],
    borderTop: `1px solid ${theme.colors.gray[8]}`,
    overflow: 'auto',
  },
}));

function TeamSurvivorNonScheduledNav({ contest }: { contest: FullContest }) {
  const { t } = useTranslation('contest');
  const { classes } = useStyles();
  const contestId = contest.id;
  const isAvailabilityFeatureEnabled = useIsFeatureEnabled(
    FEATURE.ENABLE_SURVIVOR_AVAILABILITY_PAGE
  );
  const showAvailabilityTab = typeof contest.settings?.pickReuseLimit === 'number';
  const isAvailabilityEnabled = isAvailabilityFeatureEnabled && showAvailabilityTab;
  const isSpectatorMode = contest?.public_viewing && contest?.entries.user === 0;

  return (
    <div className={classes.container}>
      <Container>
        <GamecenterNonScheduledNavList>
          <GamecenterNonScheduledNavListItem
            href={Routes.gamecenterStandingsOverall(contestId)}
            data-test-id={dt.contest.gamecenter.common.navigation.nonScheduled.standings}
          >
            {t('gamecenter.layoutNonScheduled.nav.standings')}
          </GamecenterNonScheduledNavListItem>
          {isSpectatorMode ? null : (
            <GamecenterNonScheduledNavListItem
              href={Routes.gamecenterEntriesOverall(contestId)}
              data-test-id={dt.contest.gamecenter.common.navigation.nonScheduled.entries}
            >
              {t('gamecenter.layoutNonScheduled.nav.entries')}
            </GamecenterNonScheduledNavListItem>
          )}
          {isAvailabilityEnabled && (
            <GamecenterNonScheduledNavListItem
              href={Routes.gamecenterAvailabilityAvailable(contestId)}
              activeForRoutes={[Routes.gamecenterAvailabilityUnavailable(contestId)]}
              data-test-id={dt.contest.gamecenter.common.navigation.nonScheduled.availability}
            >
              {t('gamecenter.layoutNonScheduled.nav.availability')}
            </GamecenterNonScheduledNavListItem>
          )}
          <GamecenterNonScheduledNavListItem
            href={Routes.gamecenterStats(contestId)}
            activeForRoutes={[Routes.gamecenterStatsAvailability(contestId)]}
            data-test-id={dt.contest.gamecenter.common.navigation.nonScheduled.stats}
          >
            {t('gamecenter.layoutNonScheduled.nav.stats')}
          </GamecenterNonScheduledNavListItem>
          <GamecenterMarketplaceNavItem contestId={contestId} />
          <GamecenterNonScheduledNavListItem
            href={Routes.contestDetail(contestId)}
            data-test-id={dt.contest.gamecenter.common.navigation.nonScheduled.detail}
          >
            {t('gamecenter.layoutNonScheduled.nav.detail')}
          </GamecenterNonScheduledNavListItem>
        </GamecenterNonScheduledNavList>
      </Container>
    </div>
  );
}

export default TeamSurvivorNonScheduledNav;
